<template>
  <div class="calendar" :class="{ 'calendar--dense': isDense }">
    <div v-if="loadingCalendar" class="calendar__loader">
      <Loader></Loader>
    </div>
    <Calendar
      v-else
      :columns="getColumnsCount"
      :step="1"
      class="calendar__redes-calendar"
      :attributes="attributes"
      @editCard="openCardPage"
      :mode="getCalendarMode"
      :isDense="isDense"
      :dateNavigation="dateNavigation"
    >
      <template v-if="!isDense" v-slot:legend>
        <VesCalendarLegend />
      </template>

      <template v-slot:customPopover="scope">
        <ul class="calendar__groups">
          <li v-for="group in getGroups(scope)" :key="group.groupId">
            <VesCalendarGroup :statusId="group.groupId" :list="group.groupData" />
          </li>
        </ul>
      </template>
    </Calendar>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapMutations } from 'vuex';

  import Calendar from '@/common/components/redesigned/Calendar.vue';
  import resizeEventListener from '@/common/mixins/resizeEventListener';

  import Loader from '@/components/Loader.vue';

  import { CALENDAR_LEGEND_COLORS, CALENDAR_LEGEND_ID, CALENDAR_LEGEND_ORDER } from '../../../constants/constants';
  import {
    EVENTS_VES_ACTIONS_NAME,
    EVENTS_VES_GETTERS_NAME,
    EVENTS_VES_MUTATIONS_NAME,
  } from '../../../store/events/name';
  import { getEventCardRoute } from '../../../utils/router';
  import VesCalendarGroup from './VesCalendarGroup.vue';
  import VesCalendarLegend from './VesCalendarLegend.vue';

  export default {
    name: 'VesCalendar',
    mixins: [resizeEventListener],
    components: {
      Calendar,
      Loader,
      VesCalendarGroup,
      VesCalendarLegend,
    },
    props: {
      isDense: {
        type: Boolean,
        default: false,
      },
      params: {
        type: Object,
      },
      dateNavigation: {
        type: String,
      },
    },
    data() {
      return {
        filter: {
          regionRfId: this.$route?.query?.regionId ?? null,
        },
        listForCalendar: [],
        loadingCalendar: false,
        windowWidth: window.innerWidth,
        abortController: new AbortController(),
      };
    },

    async created() {
      this.loadCalendar();
    },

    computed: {
      ...mapGetters({
        getListOfEvents: EVENTS_VES_GETTERS_NAME.getListOfEvents,
      }),

      getColumnsCount() {
        if (this.isDense) return 1;
        if (this.windowWidth >= 1280) return 3;
        if (this.windowWidth > 900) return 2;
        return 1;
      },

      getCalendarMode() {
        return this.isDense ? 'single' : null;
      },

      getFilterData() {
        const dataForm = { ...this.filter };
        if (dataForm.IsIncreasingOrder) dataForm.IsIncreasingOrder = dataForm.IsIncreasingOrder === 2;
        return dataForm;
      },

      eventsByDate() {
        return (this.listForCalendar ?? []).reduce((acc, event) => {
          const startEventDate = this.getStableDateKey(event.startEventDate);
          acc[startEventDate] = acc[startEventDate] ?? [];
          acc[startEventDate].push(event);
          return acc;
        }, {});
      },

      attributes() {
        const attrs = (this.listForCalendar ?? []).reduce((acc, event) => {
          const { ferLegendId, startEventDate } = event;
          const legendId = ferLegendId ?? CALENDAR_LEGEND_ID.other;

          acc[legendId] = acc[legendId] ?? {};

          const dotColor = CALENDAR_LEGEND_COLORS[legendId];
          acc[legendId].dot = acc[legendId].dot ?? {
            style: {
              backgroundColor: dotColor,
            },
          };

          acc[legendId].popover = acc[legendId].popover ?? {
            visibility: 'click',
            isInteractive: true,
            modifiers: [
              {
                name: 'flip',
                enabled: false,
              },
            ],
          };

          acc[legendId].dates = acc[legendId].dates ?? [];
          const startDate = startEventDate;
          if (!acc[legendId].dates.includes(startDate)) {
            acc[legendId].dates.push(startDate);
          }

          return acc;
        }, {});

        const ordered = [];
        CALENDAR_LEGEND_ORDER.forEach((id) => {
          if (attrs[id]) ordered.push(attrs[id]);
        });

        Object.keys(attrs).forEach((key) => {
          const numKey = Number(key);
          if (!CALENDAR_LEGEND_ORDER.includes(numKey) && !ordered.includes(attrs[key])) {
            ordered.push(attrs[key]);
          }
        });
        return ordered;
      },
    },

    methods: {
      ...mapActions({
        getEventsTable: EVENTS_VES_ACTIONS_NAME.getEventsTable,
      }),

      ...mapMutations({
        setEvents: EVENTS_VES_MUTATIONS_NAME.setEvents,
      }),

      /**
       * @public
       * обрабатывается через миксин `resizeEventListener`
       */
      onResize() {
        this.windowWidth = window.innerWidth;
      },

      async loadCalendar() {
        this.loadingCalendar = true;

        this.abortController.abort();
        this.abortController = new AbortController();

        const payload = {
          params: { ...this.getFilterData, ...this.params },
          abortController: this.abortController,
        };

        const res = await this.getEventsTable(payload);
        if (res.success) {
          this.listForCalendar = res.data.items;
        }
        this.loadingCalendar = false;
      },

      openCardPage(id) {
        this.$router.push(getEventCardRoute(id));
      },

      // необходимо получать строковое представление даты без привязки к часовому поясу
      getStableDateKey(date) {
        return new Date(date).toLocaleString();
      },

      getPopover(scope) {
        const selectedDate = this.getStableDateKey(scope.day.date);
        return this.eventsByDate[selectedDate];
      },

      getGroups(scope) {
        const events = this.getPopover(scope) ?? [];
        const grouped = events.reduce((acc, event) => {
          const legendId = event.ferLegendId ?? null;
          acc[legendId] = acc[legendId] || [];
          acc[legendId].push(event);
          return acc;
        }, {});

        const ordered = [];
        CALENDAR_LEGEND_ORDER.forEach((id) => {
          if (grouped[id]) ordered.push({ groupId: id, groupData: grouped[id] });
        });
        Object.keys(grouped).forEach((key) => {
          const numKey = Number(key);
          if (!CALENDAR_LEGEND_ORDER.includes(numKey)) {
            ordered.push({ groupId: key, groupData: grouped[key] });
          }
        });
        return ordered;
      },
    },
    watch: {
      params: {
        handler() {
          this.loadCalendar();
        },
        deep: true,
      },
    },
  };
</script>

<style lang="scss" scoped>
  .calendar {
    &--dense {
      @media (max-width: $mobile-2) {
        margin-left: -11px;
        margin-right: -20px;
        max-width: 324px;
      }
    }

    &__loader {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 466px;
      margin-bottom: 40px;
    }

    &__groups {
      width: 510px;
      max-width: 100%;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &:not(.calendar--dense) {
      :deep(.v-redes-calendar__arrow) {
        margin-top: 100px;

        @media (max-width: $laptop) {
          margin-top: 0;
          margin-left: 262px;
          position: relative;
          left: -124px;

          &.next {
            margin-left: 30px;
          }
        }

        @media (max-width: $mobile) {
          margin-left: 180px;
          left: -90px;
        }
      }
    }
  }

  .calendar__redes-calendar {
    margin-bottom: 40px;
    overflow-x: hidden;

    @media (max-width: $mobile) {
      padding: 14px;
    }

    :deep(.vc-popover-content-wrapper) {
      width: 100%;
    }

    &:not(.calendar--dense) {
      :deep(.v-redes-calendar__wrapper) {
        margin: 0 auto;

        @media (max-width: $laptop) {
          .vc-container {
            flex: 0 0 auto;
          }
        }
      }
    }

    :deep(.v-redes-calendar__legend) {
      @media (max-width: $laptop) {
        margin-top: 40px;
      }

      @media (max-width: $mobile) {
        padding-inline: 0;
      }
    }

    :deep(.vc-dot) {
      display: inline-block !important;

      &.hidden {
        display: none !important;
      }
    }
  }
</style>
